import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Product, CartItem } from '../services/storeService';

interface CartContextType {
    items: CartItem[];
    itemCount: number;
    totalAmount: number;
    addItem: (product: Product, quantity?: number, isSubscription?: boolean, selectedInterval?: any) => void;
    updateQuantity: (productId: number, quantity: number, isSubscription?: boolean, selectedInterval?: any) => void;
    removeItem: (productId: number, isSubscription: boolean, selectedInterval?: any) => void;
    clearCart: () => void;
    getItem: (productId: number) => CartItem | undefined;
    updateMembershipStatus: (memberships: Array<{ productId: number; hasActiveMembership: boolean }>) => void;
    updateSubscriptionInterval: (productId: number, selectedInterval: any) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
    children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const initializationRef = useRef(false);

    // Load cart from localStorage on mount - only once
    useEffect(() => {
        if (initializationRef.current) return;
        initializationRef.current = true;

        try {
            // Use consistent key name for store client
            const savedCart = localStorage.getItem('alyze-store-cart');
            if (savedCart) {
                const parsedCart = JSON.parse(savedCart);
                setItems(parsedCart);
            }
        } catch (error) {
            console.error('Error loading cart from localStorage:', error);
        } finally {
            setIsInitialized(true);
        }
    }, []);

    // Save cart to localStorage whenever items change (but only after initialization)
    useEffect(() => {
        if (!isInitialized) return;

        try {
            // Use consistent key name for store client
            localStorage.setItem('alyze-store-cart', JSON.stringify(items));
        } catch (error) {
            console.error('Error saving cart to localStorage:', error);
        }
    }, [items, isInitialized]);

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const addItem = (product: Product, quantity: number = 1, isSubscription: boolean = false, selectedInterval?: any) => {
        setItems(prevItems => {
            // For subscription items, check product ID, subscription status, AND interval
            // For one-time items, only check product ID and subscription status
            const existingItem = prevItems.find(item => {
                if (item.productId !== product.productId || item.isSubscription !== isSubscription) {
                    return false;
                }
                
                // If it's a subscription item, also check if intervals match
                if (isSubscription && selectedInterval) {
                    return item.selectedInterval?.billingInterval === selectedInterval.billingInterval &&
                           item.selectedInterval?.billingPeriod === selectedInterval.billingPeriod;
                }
                
                // For one-time purchases, just matching productId and isSubscription is enough
                return !isSubscription;
            });

            let newItems: CartItem[];
            if (existingItem) {
                newItems = prevItems.map(item => {
                    // Find the exact matching item
                    const isExactMatch = item.productId === product.productId && 
                                       item.isSubscription === isSubscription &&
                                       (!isSubscription || 
                                        (item.selectedInterval?.billingInterval === selectedInterval?.billingInterval &&
                                         item.selectedInterval?.billingPeriod === selectedInterval?.billingPeriod));
                    
                    if (isExactMatch) {
                        return { 
                            ...item, 
                            quantity: item.quantity + quantity,
                            selectedInterval: isSubscription ? selectedInterval : undefined
                        };
                    }
                    return item;
                });
            } else {                
                // Use the currentPrice if available (already discounted from ProductDetailPage)
                // Otherwise use membership/retail price without additional discount
                const basePrice = (product as any).currentPrice || 
                                 (product.hasMembership ? product.membershipPrice : product.retailPrice);

                const newItem: CartItem = {
                    productId: product.productId,
                    quantity,
                    price: basePrice,
                    product: {
                        ...product,
                        productType: product.productType || 'simple' // Ensure productType is included
                    },
                    isSubscription,
                    selectedInterval: isSubscription ? selectedInterval : undefined
                };
                newItems = [...prevItems, newItem];
            }

            return newItems;
        });
    };

    const removeItem = (productId: number, isSubscription: boolean, selectedInterval?: any) => {
        // Remove specific item by productId, subscription status, and interval (if subscription)
        setItems(prevItems => prevItems.filter(item => {
            if (item.productId !== productId || item.isSubscription !== isSubscription) {
                return true; // Keep this item
            }
            
            // If it's a subscription item, also check interval
            if (isSubscription && selectedInterval) {
                return !(item.selectedInterval?.billingInterval === selectedInterval.billingInterval &&
                        item.selectedInterval?.billingPeriod === selectedInterval.billingPeriod);
            }
            
            // For one-time purchases, remove if productId and subscription status match
            return isSubscription;
        }));
    };

    const updateQuantity = (productId: number, quantity: number, isSubscription: boolean = false, selectedInterval?: any) => {
        if (quantity <= 0) {
            removeItem(productId, isSubscription, selectedInterval);
            return;
        }

        setItems(prevItems =>
            prevItems.map(item => {
                // Check if this is the exact item we want to update
                const isExactMatch = item.productId === productId && 
                                   (isSubscription === undefined || item.isSubscription === isSubscription) &&
                                   (!item.isSubscription || !selectedInterval ||
                                    (item.selectedInterval?.billingInterval === selectedInterval?.billingInterval &&
                                     item.selectedInterval?.billingPeriod === selectedInterval?.billingPeriod));
                
                return isExactMatch ? { ...item, quantity } : item;
            })
        );
    };

    const clearCart = () => {
        setItems([]);
    };

    const getItem = (productId: number): CartItem | undefined => {
        return items.find(item => item.productId === productId);
    };

    const updateMembershipStatus = (memberships: Array<{ productId: number; hasActiveMembership: boolean }>) => {
        // Get the overall membership status from any membership record
        // Since all products should have the same membership status based on user's overall membership
        const userHasAnyMembership = memberships.length > 0 && memberships.some(m => m.hasActiveMembership);

        setItems(prevItems => {
            return prevItems.map(item => {
                if (item.product) {

                    const updatedProduct = {
                        ...item.product,
                        hasMembership: userHasAnyMembership
                    };

                    // Update the price based on membership status
                    const newPrice = userHasAnyMembership ? updatedProduct.membershipPrice : updatedProduct.retailPrice;

                    return {
                        ...item,
                        product: updatedProduct,
                        price: newPrice
                    };
                }
                return item;
            });
        });
    };

    const updateSubscriptionInterval = (productId: number, selectedInterval: any) => {
        setItems(prevItems =>
            prevItems.map(item => {
                // Only update subscription items with matching productId
                if (item.productId === productId && item.isSubscription === true && item.product) {
                    // Calculate the original prices (before any subscription discount)
                    let originalRetailPrice = item.product.retailPrice;
                    let originalMembershipPrice = item.product.membershipPrice;
                    
                    // If current item has a discount, restore original prices first
                    if (item.selectedInterval?.billingDiscount) {
                        originalRetailPrice = item.product.retailPrice / (1 - (item.selectedInterval.billingDiscount / 100));
                        originalMembershipPrice = item.product.membershipPrice / (1 - (item.selectedInterval.billingDiscount / 100));
                    }

                    // Apply new discount if the new interval has one
                    let newRetailPrice = originalRetailPrice;
                    let newMembershipPrice = originalMembershipPrice;
                    
                    if (selectedInterval?.billingDiscount) {
                        const retailDiscount = (originalRetailPrice * selectedInterval.billingDiscount) / 100;
                        const membershipDiscount = (originalMembershipPrice * selectedInterval.billingDiscount) / 100;
                        
                        newRetailPrice = originalRetailPrice - retailDiscount;
                        newMembershipPrice = originalMembershipPrice - membershipDiscount;
                    }

                    const updatedProduct = {
                        ...item.product,
                        retailPrice: newRetailPrice,
                        membershipPrice: newMembershipPrice
                    };
                    
                    // Update item price based on membership status
                    const newPrice = updatedProduct.hasMembership ? newMembershipPrice : newRetailPrice;
                    
                    return { 
                        ...item, 
                        selectedInterval,
                        price: newPrice,
                        product: updatedProduct
                    };
                }
                return item;
            })
        );
    };

    const value: CartContextType = {
        items,
        itemCount,
        totalAmount,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        getItem,
        updateMembershipStatus,
        updateSubscriptionInterval,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
